import { z } from '@frontend/forms';
import { ECONOMICS_TERMINOLOGY_DEFAULT, PRICING_MODE_DEFAULT, PRICING_MODES } from '@frontend/react';

/**
 * Zod schema for subscription terminology config.
 *
 * Uses .catch() to provide safe fallbacks if any field is:
 * - Missing from the JSON
 * - Wrong type (e.g., number instead of string)
 * - Malformed
 *
 * This ensures the UI never breaks even if GrowthBook returns bad data.
 */
export const SubscriptionTerminologyConfigSchema = z.object({
  add_button: z.string().catch(ECONOMICS_TERMINOLOGY_DEFAULT.add_button),
  balance_title: z.string().catch(ECONOMICS_TERMINOLOGY_DEFAULT.balance_title),
  cost_label: z.string().catch(ECONOMICS_TERMINOLOGY_DEFAULT.cost_label),
  expiration_label: z.string().catch(ECONOMICS_TERMINOLOGY_DEFAULT.expiration_label),
  hide_credits: z.boolean().catch(ECONOMICS_TERMINOLOGY_DEFAULT.hide_credits),
  history_title: z.string().catch(ECONOMICS_TERMINOLOGY_DEFAULT.history_title),
  insufficient_label: z.string().catch(ECONOMICS_TERMINOLOGY_DEFAULT.insufficient_label),
  monthly_progress_direction: z
    .enum(['drain', 'fill'])
    .nullable()
    .catch(ECONOMICS_TERMINOLOGY_DEFAULT.monthly_progress_direction),
  plan_section_subtitle: z.string().catch(ECONOMICS_TERMINOLOGY_DEFAULT.plan_section_subtitle),
  plan_subtitle_template: z.string().catch(ECONOMICS_TERMINOLOGY_DEFAULT.plan_subtitle_template),
  referral_reward: z.string().catch(ECONOMICS_TERMINOLOGY_DEFAULT.referral_reward),
  unit_term: z.string().catch(ECONOMICS_TERMINOLOGY_DEFAULT.unit_term),
  unit_term_plural: z.string().catch(ECONOMICS_TERMINOLOGY_DEFAULT.unit_term_plural),
});

export type SubscriptionTerminologyConfig = z.infer<typeof SubscriptionTerminologyConfigSchema>;

/**
 * Validate and parse config from GrowthBook.
 * Returns validated config with safe defaults for any invalid/missing fields.
 */
export const parseTerminologyConfig = (rawConfig: unknown): SubscriptionTerminologyConfig => {
  try {
    if (rawConfig === null || rawConfig === undefined || typeof rawConfig !== 'object') {
      return SubscriptionTerminologyConfigSchema.parse({});
    }
    return SubscriptionTerminologyConfigSchema.parse(rawConfig);
  } catch {
    return ECONOMICS_TERMINOLOGY_DEFAULT;
  }
};

/**
 * Zod schema for pricing mode validation.
 * Falls back to default if GrowthBook returns an invalid value.
 */
export const PricingModeSchema = z.enum(PRICING_MODES).catch(PRICING_MODE_DEFAULT);

export type ValidatedPricingMode = z.infer<typeof PricingModeSchema>;

/**
 * Validate pricing mode from GrowthBook.
 * Returns validated mode or default if invalid.
 */
export const parsePricingMode = (rawMode: unknown): ValidatedPricingMode => {
  return PricingModeSchema.parse(rawMode);
};
